import React, { useMemo, useState, useEffect, useRef } from "react"
import { Check, Search, X } from "lucide-react"

import { cn } from "../../utils/css"
import { Input } from "./Input"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../shadcn/Command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../shadcn/Popover"

export interface SearchBoxListOption<T = unknown> {
  label: string;
  value: string;
  disabled?: boolean;
  data?: T;
}

export interface SearchBoxProps {
  list: SearchBoxListOption[];
  noFilterMatchesLabel?: string;
  placeholder?: string;
  value: SearchBoxListOption | null | undefined;
  onSelect: (selected: SearchBoxListOption | null | undefined, isNew?: boolean) => void;
  isNewItemsAllowed?: boolean;
  inputValue: string
  setInputValue: (value: string) => void;
  shouldFilter?: boolean;
  className?: string;
  buttonClassName?: string;
  isLoading?: boolean;
}

const SearchBox = ({ list, noFilterMatchesLabel = "No matches", placeholder = "Select an option...", value, onSelect, inputValue, setInputValue, isNewItemsAllowed = false, shouldFilter = true, className, buttonClassName, isLoading = false }: SearchBoxProps) => {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const commandRef = useRef<HTMLDivElement>(null)
  const [displayText, setDisplayText] = useState("")

  // Use displayText for the input display, but inputValue for searches
  const displayValue = displayText || inputValue

  // Sync displayText with inputValue when user types (not when selecting)
  useEffect(() => {
    if (!value) {
      setDisplayText(inputValue);
    }
  }, [inputValue, value]);

  const isNoMatches = useMemo(() => {
    // Don't show "No matches" if we're currently loading
    if (isLoading) return false;
    // Only show "No matches" if we have input AND the list is explicitly empty (search completed)
    return inputValue.trim() && list.length === 0;
  }, [inputValue, list, isLoading]);

  // Open popover when loading starts
  useEffect(() => {
    if (isLoading && inputValue.trim()) {
      setOpen(true);
    }
  }, [isLoading, inputValue]);

  // Maintain focus on input when list updates
  useEffect(() => {
    if (open && inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  }, [list, open]);

  return (
    <div className={cn("relative flex items-center gap-1", className)}>
      <Popover
        open={open}
        onOpenChange={(newOpen) => {
          // Only allow opening if there's content or results or loading
          if (newOpen && !inputValue.trim() && list.length === 0 && !isLoading) {
            return; // Prevent opening with no content
          }
          // Prevent immediate closing when clicking on input (but only if we should be open)
          if (!newOpen && document.activeElement === inputRef.current && (inputValue.trim() || list.length > 0 || isLoading)) {
            return;
          }
          setOpen(newOpen);
        }}
      >
        <div className="relative w-full">
          <PopoverTrigger asChild>
            <Input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={displayValue}
              onChange={(e) => {
                setDisplayText(e.target.value);
                setInputValue(e.target.value);
                // Open/close based on whether there's content
                const hasContent = !!e.target.value.trim();
                if (open !== hasContent) {
                  setOpen(hasContent);
                }
              }}
              onFocus={() => {
                // Don't open on focus - only when typing or if there are already results
              }}
              onClick={() => {
                // Don't open on click - only when typing or if there are already results
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  if (!open && (inputValue.trim() || list.length > 0)) {
                    setOpen(true);
                  } else if (list.length > 0) {
                    // Focus the command component and trigger down arrow
                    setTimeout(() => {
                      if (commandRef.current) {
                        commandRef.current.focus();
                        // Dispatch arrow down event to the command component
                        const event = new KeyboardEvent('keydown', {
                          key: 'ArrowDown',
                          bubbles: true
                        });
                        commandRef.current.dispatchEvent(event);
                      }
                    }, 0);
                  }
                } else if (e.key === 'Escape') {
                  setOpen(false);
                }
              }}
              className={cn("w-full", value?.value ? "pr-12" : "pr-8", buttonClassName)}
            />
          </PopoverTrigger>
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            {value && value?.value && (
              <X
                className="cursor-pointer h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation()
                  onSelect(null)
                  setInputValue("")
                  setDisplayText("")
                  setOpen(false)
                }}
                aria-label="Clear selection"
              />
            )}
            <Search className="h-4 w-4 opacity-50 pointer-events-none" />
          </div>
        </div>
        <PopoverContent
          className="min-w-[var(--radix-popover-trigger-width)] p-0 focus:outline-none"
          style={{ zIndex: 9999 }}
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <Command ref={commandRef} shouldFilter={shouldFilter} className="focus:outline-none">
            <CommandList>
              {isLoading && (
                <CommandEmpty className="py-6 text-center text-sm">
                  Searching...
                </CommandEmpty>
              )}
              {!isLoading && !isNewItemsAllowed && isNoMatches && (
                <CommandEmpty className="py-6 text-center text-sm">
                  {noFilterMatchesLabel}
                </CommandEmpty>
              )}
              <CommandGroup>
                {list.map((item, index) => (
                  <CommandItem
                    key={`${item.value}-${index}`}
                    value={item.value}
                    disabled={item.disabled}
                    className="px-4 py-3 cursor-pointer"
                    onSelect={(currentValue) => {
                      if (item.disabled) return;
                      const selected = list.find(({ value }) => value === currentValue)
                      const newSelection = selected === value ? null : selected
                      onSelect(newSelection)
                      // Update display text but don't trigger a new search
                      if (newSelection) {
                        setDisplayText(newSelection.label)
                        // Don't update inputValue to keep the original search results
                      }
                      setOpen(false)
                    }}
                  >
                    {item.label}
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        value && value.value === item.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandGroup forceMount>
                {isNewItemsAllowed && isNoMatches && (
                  <CommandItem forceMount value={inputValue} className="cursor-pointer" onSelect={(value) => {
                    const newItem = { label: value, value: value }
                    onSelect(newItem, true);
                    setDisplayText(newItem.label)
                    setOpen(false);
                  }}>
                    Add "{inputValue}"
                  </CommandItem>)
                }
              </CommandGroup>

            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default SearchBox